import {
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useRef } from "react";
import { ArrowRight, BarChart2, BookOpen, Sliders, Users } from "react-feather";
import { useNavigate } from "react-router-dom";

const metricInsights = {
  "CALIBRATE CAPACITY": {
    Definition: [
      {
        desc: "Calibrate Capacity focuses on understanding and maximizing the available operational supply of your practice. It measures how efficiently your time, space, and team are utilized to meet demand. The goal is to reach optimal workload without overextending people or resources.",
      },
    ],
    "Key KPIs": {
      description: "Key KPIs to track capacity.",
      items: [
        {
          title: "Capacity Utilization Rate",
          desc: "Total booked appointments ÷ total available slots.\n\n Shows how effectively your schedule is being used. Track by provider and time block to identify gaps, bottlenecks, or overloading. Compare to target capacity weekly to guide scheduling and resource planning.",
        },
        {
          title: "Revenue per Visit",
          desc: "Total income ÷ total visits.\n\n Indicates value generated per appointment and whether growth comes from pricing, case acceptance, or visit mix. Monitor alongside capacity utilization to balance volume and value effectively.",
        },
        {
          title: "Average Weekly Volume",
          desc: "Total treatment volume per week.\n\n Reveals total activity and throughput. Compare to calculated capacity to see how close you are to optimal load. Use trends to adjust staffing, hours, and marketing cadence for steady patient flow.",
        },
        {
          title: "Close Rate",
          desc: "New patients booked ÷ new patient leads.\n\n Measures how effectively inquiries convert to active patients. Track by source and team member to refine communication, response time, and conversion processes for reliable weekly volume.",
        },
        {
          title: "Schedule Balance",
          desc: "Ratio of new patients : existing patients : auxiliary visits.\n\n Evaluates the balance of visit types that fill your schedule. Optimizing this mix supports stable intake, continuity of care, and efficient provider use across all available treatment time.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve capacity.",
      items: [
        {
          title: "Build a Capacity Framework",
          desc: "Define and maintain an ideal capacity model that clearly outlines the maximum number of patient visits, treatments, or service units your practice can deliver. Update regularly as team size, hours, or systems change to ensure accuracy and focus.",
        },
        {
          title: "Reduce Cancellations and No-Shows",
          desc: "Implement confirmation systems, reminders, and structured rebooking processes to minimize lost appointments. Review missed visit data weekly to identify recurring trends and apply corrective actions that improve schedule reliability.",
        },
        {
          title: "Improve Patient Flow",
          desc: "Ensure the physical layout, patient handoff process, and pre- and post-appointment logistics minimize idle time. Optimize check-in, waiting, and treatment room transitions to create a smoother, more predictable daily flow.",
        },
        {
          title: "Enhance Provider Efficiency",
          desc: "Evaluate how providers and support staff interact to maximize productive time. Improve delegation and leverage back-office assistance to reduce administrative burden, increase throughput, and sustain quality under higher capacity.",
        },
        {
          title: "Leverage Pricing Strategies",
          desc: "Adjust pricing based on supply and demand dynamics to improve utilization. Temporary discounts, referral incentives, or flexible pricing for slower times can stimulate new patient inflow and fill available capacity efficiently.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting capacity.",
      items: [
        {
          title: "Capacity ↔ Income",
          desc: "Higher capacity enables more patient volume and visit throughput, directly increasing income potential and overall practice growth.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Capacity ↔ Team",
          desc: "Team size and efficiency determine how much capacity can be delivered. The right structure ensures consistent output without overextension.",
          url: "/lucro-metrics/team/",
          buttonText: "Explore Team",
        },
        {
          title: "Capacity ↔ Marketing",
          desc: "Marketing drives the demand needed to fill available capacity. Strong, consistent marketing ensures schedules remain full and predictable.",
          url: "/lucro-metrics/marketing/",
          buttonText: "Explore Marketing",
        },
      ],
    },
  },
  "POSITION PEOPLE": {
    Definition: [
      {
        desc: "Build and align a team that meets your capacity goals efficiently and effectively. The right structure, skills, and motivation drive consistent performance, enhance patient experience, and ensure long-term stability and growth for the practice.",
      },
    ],
    "Key KPIs": {
      description: "Key KPIs to track team performance.",
      items: [
        {
          title: "Perfect P&L Ratio",
          desc: "Total team expenses ÷ Total income.\n\n Indicates how closely team costs align with the Perfect P&L benchmark. A higher ratio often signals inefficiency or overstaffing, while a lower ratio may show available capacity or opportunity for growth.",
        },
        {
          title: "Revenue per Team Member",
          desc: "Total income ÷ Number of team members.\n\n Evaluates productivity and contribution per person. Higher averages reflect balanced workload and efficient staffing, while lower figures may indicate underperformance or operational inefficiencies.",
        },
        {
          title: "Individual Team KPIs",
          desc: "Role dependent.\n\n Tracks each team member’s specific performance metrics tied to their responsibilities. Role-based KPIs create clarity, accountability, and alignment with overall business objectives and outcomes.",
        },
        {
          title: "Training Hours per Team Member",
          desc: "Total training hours ÷ Total team members (per period).\n\n Measures investment in professional development. Regular, structured training builds consistency, confidence, and skill, improving long-term performance and team retention.",
        },
        {
          title: "Percentage of Schedule Filled (per Provider)",
          desc: "Total booked appointments ÷ Total available slots (per provider).\n\n Shows how effectively each provider’s schedule is utilized. High utilization reflects strong demand and scheduling efficiency, while gaps indicate unused capacity or workflow issues.",
        },
      ],
    },
    Levers: {
      description: "Key levers to improve team efficiency and alignment.",
      items: [
        {
          title: "Develop Clear Performance Reviews",
          desc: "Create structured performance review templates that define each role’s objectives, KPIs, and core skills. This clarity improves accountability, enhances communication, and ensures alignment between team output and business goals.",
        },
        {
          title: "Refine Compensation Packages",
          desc: "Align compensation design with performance outcomes to reward productivity and efficiency. Linking pay to measurable results reinforces accountability and drives stronger engagement across the team.",
        },
        {
          title: "Design Individual Capacity Frameworks",
          desc: "Build customized frameworks for each team member that define ideal schedules, output expectations, and workload balance. Aligning individual capacity with practice goals improves efficiency and team synergy.",
        },
        {
          title: "Strengthen Onboarding and Training",
          desc: "Implement a consistent onboarding and training process focused on skills development, role clarity, and practical application. Continuous education enhances confidence, reduces errors, and boosts overall team performance.",
        },
        {
          title: "Build a Culture of Accountability",
          desc: "Foster an environment where each team member takes ownership of outcomes and commitments. Regular check-ins, feedback, and recognition strengthen responsibility, trust, and long-term team cohesion.",
        },
      ],
    },
    Relationships: {
      description: "Key relationships affecting team performance and cost.",
      items: [
        {
          title: "People ↔ Team",
          desc: "Your team represents one of the largest cost centers in your business. Understanding total team costs helps maintain efficiency and protect overall profitability.",
          url: "/lucro-metrics/team/",
          buttonText: "Explore Team",
        },
        {
          title: "People ↔ Income",
          desc: "Team structure and performance directly determine your practice’s capacity to generate income. A well-organized team converts effort into consistent, scalable revenue.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "People ↔ Operations",
          desc: "Operational systems exist to support and enhance team performance. Strong processes reduce inefficiency, allowing your people to focus on productivity and patient outcomes.",
          url: "/lucro-metrics/overhead/",
          buttonText: "Explore Operations",
        },
      ],
    },
  },
  "DRIVE DEMAND": {
    Definition: [
      {
        desc: "Create consistent patient flow through internal, external, and digital marketing systems that attract new patients, reactivate past ones, and retain existing patients to keep overall capacity full, predictable, and steadily growing.",
      },
    ],
    "Key KPIs": {
      description: "Key KPIs to track marketing and sales efficiency.",
      items: [
        {
          title: "Perfect P&L Ratio",
          desc: "Total Marketing Spend ÷ Total Income.\n\n Shows what portion of income is spent on marketing and how closely this aligns with the Perfect P&L benchmark. A higher ratio may indicate inefficiency, while a lower one may suggest under-investment in growth.",
        },
        {
          title: "Break Even ROI",
          desc: "100 ÷ Profit Margin.\n\n Indicates the minimum marketing return required to cover costs. For example, with a 25% profit margin, marketing must generate a 4x ROI to break even and validate spend efficiency.",
        },
        {
          title: "Sales Conversion Rate",
          desc: "New Patients ÷ Total Leads Generated.\n\n Measures how effectively marketing leads are converted into paying patients. High conversion rates show strong sales performance, while low rates may indicate gaps in communication, follow-up, or team training.",
        },
        {
          title: "True ROI",
          desc: "Average Lifetime Value × Profit Margin.\n\n Captures long-term marketing return by connecting patient lifetime value and profitability. This KPI helps evaluate which strategies drive sustainable, high-quality patient relationships.",
        },
        {
          title: "Channel Specific ROI",
          desc: "Revenue Attributed to Channel ÷ Cost of Channel.\n\n Assesses the ROI of each marketing and sales channel. Comparing paid ads, referrals, and internal campaigns reveals which sources deliver the most efficient, repeatable results.",
        },
      ],
    },
    Levers: {
      description:
        "Key levers to improve marketing consistency and effectiveness.",
      items: [
        {
          title: "Refine Core Marketing Strategies",
          desc: "Focus on the two or three most effective marketing strategies that consistently deliver results. Strengthen supporting systems, refine messaging, and measure ROI to build reliable, long-term growth through consistency.",
        },
        {
          title: "Develop Authentic Sales Training",
          desc: "Train yourself and your team to approach sales as education, not persuasion. Build confidence in communicating value, handling objections, and converting inquiries into committed patients through honest, outcome-focused conversations.",
        },
        {
          title: "Embed Experience-Based Marketing",
          desc: "Train your team to create memorable patient experiences that drive organic growth through reviews, referrals, and retention. Small, consistent improvements in communication and environment turn daily operations into powerful marketing.",
        },
        {
          title: "Build a Culture of Referrals",
          desc: "Encourage every team member to participate in patient referrals. Create structured prompts, clear scripts, and recognition systems that normalize asking for referrals, driving low-cost, high-quality new patient flow.",
        },
        {
          title: "Ensure Marketing and Sales Balance",
          desc: "Maintain a steady rhythm across internal, external, and digital marketing while integrating sales execution at every stage. Align marketing efforts with sales follow-up, planning, and review to create consistent lead flow and predictable conversions.",
        },
      ],
    },
    Relationships: {
      description:
        "Key relationships connecting marketing, team, and income performance.",
      items: [
        {
          title: "Sales and Marketing ↔ Income",
          desc: "Sales and marketing together drive patient flow and income growth. Consistent, targeted execution ensures predictable revenue and sustainable profitability.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Sales and Marketing ↔ Team",
          desc: "Effective marketing creates demand that the team must deliver. Balanced staffing, training, and scheduling convert new leads into care efficiently and reliably.",
          url: "/lucro-metrics/team/",
          buttonText: "Explore Team",
        },
        {
          title: "Sales and Marketing ↔ Operations",
          desc: "Strong operational systems enable marketing and sales to perform. Streamlined scheduling, communication, and workflows convert interest into lasting relationships.",
          url: "/lucro-metrics/overhead/",
          buttonText: "Explore Operations",
        },
      ],
    },
  },
  "OPTIMIZE OPERATIONS": {
    Definition: [
      {
        desc: "Streamline systems, workflows, and logistics so the practice runs efficiently, predictably, and profitably. Strong operations cut waste, lift team performance, and deliver consistent patient experience without adding unnecessary complexity or cost.",
      },
    ],
    "Key KPIs": {
      description:
        "Key KPIs to track operational efficiency and system performance.",
      items: [
        {
          title: "Perfect P&L Ratio (Including Subcategories)",
          desc: "Operational costs as a percentage of total income.\n\n Measures how efficiently the practice operates within its financial benchmarks. Reviewing both overall and subcategory ratios reveals where cost discipline or process refinement is needed.",
        },
        {
          title: "Billing Collection Rate",
          desc: "Collected revenue ÷ Total billed revenue.\n\n Assesses how effectively the practice converts billings into received income. Strong billing systems and follow-up processes ensure healthy cash flow and accurate income reporting.",
        },
        {
          title: "Fixed vs Variable Composition",
          desc: "Percentage of total costs that are fixed versus variable.\n\n Shows how adaptable your operations are to income changes. A balanced mix offers flexibility to scale up during growth or adjust quickly during slower periods.",
        },
        {
          title: "Operational Structure and Meeting Rhythms",
          desc: "Evaluated through scheduled team and leadership meetings.\n\n Measures the effectiveness of internal structure and communication flow. Clear rhythms, defined roles, and focused priorities drive alignment, accountability, and operational consistency.",
        },
        {
          title: "Team Feedback and KPIs",
          desc: "Collected through structured individual and team reviews.\n\n Captures team input on workflow, systems, and efficiency. Regular feedback ensures continuous improvement, identifies friction points early, and aligns performance with practice goals.",
        },
      ],
    },
    Levers: {
      description:
        "Key levers to enhance operational efficiency and consistency.",
      items: [
        {
          title: "Regular Expense Review",
          desc: "Review operational expenses monthly to identify unnecessary or outdated costs. Ongoing oversight maintains lean operations, ensures resources are allocated effectively, and preserves profitability across every functional area.",
        },
        {
          title: "Tighten Expense Controls",
          desc: "Establish clear spending policies and approval processes. Evaluate each expense for necessity and return, ensuring only essential, performance-enhancing costs are approved to protect profitability and reduce operational waste.",
        },
        {
          title: "Document and Refine SOPs",
          desc: "Create clear, accessible documentation for every recurring process in your practice. Review and update quarterly to reflect changes in workflow, keeping systems relevant, consistent, and scalable as the business evolves.",
        },
        {
          title: "Improve Structure and Meeting Rhythms",
          desc: "Strengthen operational alignment by running consistent leadership and team meetings. Defined agendas, review cycles, and accountability systems ensure plans turn into measurable progress and reliable execution.",
        },
        {
          title: "Integrate Technology Thoughtfully",
          desc: "Adopt systems that streamline scheduling, billing, and communication. Use automation to reduce manual workload, eliminate duplication, and create smoother workflows that enhance productivity and consistency.",
        },
      ],
    },
    Relationships: {
      description:
        "Key relationships connecting operations with performance, cost, and profit.",
      items: [
        {
          title: "Operations ↔ Team",
          desc: "Clear systems and effective tools amplify team productivity and reduce friction, translating effort into consistent output and better patient experience.",
          url: "/lucro-metrics/team/",
          buttonText: "Explore Team",
        },
        {
          title: "Operations ↔ Total Expenses",
          desc: "Operational discipline shapes a large share of total costs. Keeping overhead efficient preserves margins and creates room for targeted reinvestment.",
          url: "/lucro-metrics/expense/",
          buttonText: "Explore Expenses",
        },
        {
          title: "Operations ↔ Profit",
          desc: "Every minute and dollar saved through better processes flows to the bottom line. Lean, reliable operations compound into stronger profitability.",
          url: "/lucro-metrics/profit/",
          buttonText: "Explore Profit",
        },
      ],
    },
  },
  "SCALE SUSTAINABLY": {
    Definition: [
      {
        desc: "Expand intelligently once capacity, people, demand, and operations are strong and predictable. Growth should strengthen stability, not create strain. Sustainable scaling comes from doing more of what works, better and more efficiently.",
      },
    ],
    "Key KPIs": {
      description:
        "Key KPIs to measure readiness and sustainability for growth and expansion.",
      items: [
        {
          title: "Net Profit Margin",
          desc: "Net profit ÷ total income.\n\n Measures overall profitability and readiness for expansion. Strong margins indicate the business can fund growth sustainably without straining cash flow or increasing debt.",
        },
        {
          title: "Perfect P&L Ratio (Products)",
          desc: "Product costs ÷ total income.\n\n Assesses how much income is currently generated from product sales. A balanced product ratio shows healthy diversification and signals potential for scaling physical or complementary offerings.",
        },
        {
          title: "Capacity Utilization Rate",
          desc: "Total booked appointments ÷ total available slots.\n\n Indicates how close the practice is to full capacity. Expansion should only occur when utilization is consistently high and operational efficiency is near optimal levels.",
        },
        {
          title: "True ROI (Marketing)",
          desc: "Total income generated from campaigns ÷ total marketing spend.\n\n Evaluates the effectiveness of marketing investments. Strong, consistent ROI confirms that existing demand generation systems can sustain and justify expansion efforts.",
        },
        {
          title: "Cash Reserve Ratio",
          desc: "Total cash reserves ÷ monthly operating expenses.\n\n Monitors financial strength before scaling. Maintaining reserves of at least 1.5× monthly operating costs ensures growth does not compromise liquidity or stability.",
        },
      ],
    },
    Levers: {
      description:
        "Key levers to ensure expansion strengthens stability and efficiency.",
      items: [
        {
          title: "Optimize Efficiency Before Scaling",
          desc: "Maximize performance in every Lucro Flow area before pursuing expansion. Strengthen existing systems, eliminate inefficiencies, and confirm each pillar including capacity, team, demand, and operations is performing at its peak.",
        },
        {
          title: "Explore Growth Before Addition",
          desc: "Pursue deeper growth from your current model before adding new services or space. Adjust pricing, extend care plans, and improve collections or retention to expand revenue without increasing complexity or overhead.",
        },
        {
          title: "Map Team and Operational Requirements",
          desc: "Plan in advance for how growth will impact team structure, facility needs, and system capacity. Mapping these requirements ensures scalability is supported by clear staffing, workflow, and logistical alignment.",
        },
        {
          title: "Align Marketing and Sales to Growth Goals",
          desc: "Define which marketing channels and sales strategies will drive new patient demand for expanded capacity. Coordinated marketing ensures growth momentum matches your operational readiness.",
        },
        {
          title: "Recalibrate the Lucro Flow",
          desc: "After any phase of growth, reassess every pillar including Capacity, People, Demand, Operations, and Growth to confirm balance, efficiency, and sustainability remain aligned with new business levels.",
        },
      ],
    },
    Relationships: {
      description:
        "Key relationships connecting sustainable growth with income, marketing, and profitability.",
      items: [
        {
          title: "Growth ↔ Income",
          desc: "Scaling expands income potential, but only when existing systems can handle added volume. Sustainable growth relies on operational readiness and strong financial margins.",
          url: "/lucro-metrics/income/",
          buttonText: "Explore Income",
        },
        {
          title: "Growth ↔ Marketing",
          desc: "Marketing drives the demand that justifies expansion. Strategic campaigns must align with capacity and team strength to ensure consistent patient flow and retention post-growth.",
          url: "/lucro-metrics/marketing/",
          buttonText: "Explore Marketing",
        },
        {
          title: "Growth ↔ Profit",
          desc: "Profit funds expansion and safeguards long-term stability. Scaling with discipline maintains profitability and converts growth into enduring equity and value.",
          url: "/lucro-metrics/profit/",
          buttonText: "Explore Profit",
        },
      ],
    },
  },
};

const metricStyles = {
  "CALIBRATE CAPACITY": {
    svg: "/streamline-ultimate_gauge-dashboard-bold.svg",
    gradient: "linear-gradient(180deg, #00B5D8 -34.9%, #065666 112.73%)",
  },
  "POSITION PEOPLE": {
    svg: "/raphael_people.svg",
    gradient: "linear-gradient(180deg, #FFC45B -19.61%, #E65B00 131.76%)",
  },
  "DRIVE DEMAND": {
    svg: "/fluent_arrow-trending-lines-24-filled.svg",
    gradient: "linear-gradient(141.54deg, #233DBC 10.96%, #627CFB 54.34%)",
  },
  "OPTIMIZE OPERATIONS": {
    svg: "/bi_gear-fill.svg",
    gradient: "linear-gradient(180deg, #38B2AC -19.61%, #00413E 131.76%)",
  },
  "SCALE SUSTAINABLY": {
    svg: "/game-icons_stump-regrowth.svg",
    gradient: "linear-gradient(180deg, #EF5BA2 -19.61%, #702459 131.76%)",
  },
};

const tabIcons = {
  Definition: BookOpen,
  "Key KPIs": BarChart2,
  Levers: Sliders,
  Relationships: Users,
};

export default function LucroFlowModal({
  metricName = "",
  desc = "",
  isOpen = false,
  onClose = () => {},
  drillDownEnabled = true,
}) {
  const currentMetricInsights = metricInsights[metricName] || {};
  const hasInsights = Object.keys(currentMetricInsights).length > 0;
  const currentMetric = metricStyles[metricName];
  const tabPanelScrollRef = useRef(null);

  const capitalizePerWord = (str) => {
    if (!str) return "";
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const navigate = useNavigate();

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent
        maxWidth="640px"
        maxHeight="650px"
        borderRadius="16px"
        overflow="hidden"
        p="0"
        display="flex"
        flexDirection="column"
      >
        <ModalCloseButton />

        <ModalHeader p={0} flexShrink={0}>
          <VStack align="flex-start" mt="24px" spacing="16px" mb={"20px"}>
            <Stack
              px="24px"
              direction={{ base: "column", md: "row" }}
              spacing={3}
              align={{ base: "flex-start", md: "center" }}
            >
              <Box
                w="64px"
                h="64px"
                borderRadius="8px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                background={currentMetric?.gradient || "#536DEC14"}
                boxShadow="0px 1px 12px 0px #00000014"
              >
                {currentMetric?.svg ? (
                  <img
                    src={currentMetric.svg}
                    alt={metricName}
                    width="32"
                    height="32"
                    style={{ objectFit: "contain" }}
                  />
                ) : (
                  <Text color="#6B7280">?</Text>
                )}
              </Box>

              <VStack align="flex-start" spacing={1} mt={{ base: 2, md: 0 }}>
                <Text fontSize="24px" color="#171923" fontWeight="600">
                  {capitalizePerWord(metricName) || "Unknown Metric"}
                </Text>
                <Text fontSize="14px" color="#2D3748" fontWeight="400">
                  {desc || "Unknown Metric"}
                </Text>
              </VStack>
            </Stack>
          </VStack>
          <Divider />
        </ModalHeader>

        <ModalBody
          p="20px"
          display="flex"
          flexDirection="column"
          flex="1"
          overflow="hidden"
          css={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": {
              background: "#A0AEC0",
              borderRadius: "10px",
            },
            "&::-webkit-scrollbar-thumb:hover": { background: "#718096" },
          }}
        >
          <Tabs
            isFitted
            variant="unstyled"
            colorScheme="blue"
            display="flex"
            flexDirection="column"
            flex="1"
            overflow="hidden"
          >
            <Box bg="#E6E9EC" p="4px" mb="10px" borderRadius="full">
              <TabList
                display="flex"
                flexDirection="row"
                overflowX={{ base: "auto", md: "hidden" }}
                whiteSpace="nowrap"
                gap={1}
                css={{
                  "&::-webkit-scrollbar": { display: "none" },
                  "-ms-overflow-style": "none",
                  "scrollbar-width": "none",
                }}
              >
                {hasInsights ? (
                  Object.keys(currentMetricInsights).map((tab, idx) => {
                    const IconComponent = tabIcons[tab];
                    return (
                      <Tab
                        key={idx}
                        borderRadius="full"
                        fontWeight="500"
                        fontSize="14px"
                        py="8px"
                        height="28px"
                        color="black"
                        bg="transparent"
                        _selected={{ bg: "white", fontWeight: "600" }}
                        _hover={{ bg: "white" }}
                        transition="background 0.2s ease"
                        onClick={() => {
                          if (tabPanelScrollRef.current) {
                            tabPanelScrollRef.current.scrollTop = 0;
                          }
                        }}
                      >
                        <HStack spacing={2} justify="center">
                          {IconComponent && <IconComponent size={14} />}
                          <Text>{tab}</Text>
                        </HStack>
                      </Tab>
                    );
                  })
                ) : (
                  <Text px={4} color="#6B7280">
                    No insights available
                  </Text>
                )}
              </TabList>
            </Box>

            <TabPanels flex="1" overflowY="auto" px={0} ref={tabPanelScrollRef}>
              {hasInsights ? (
                Object.entries(currentMetricInsights).map(([tab, data]) => {
                  const description = data.description || "";

                  const items = Array.isArray(data) ? data : data.items || [];

                  return (
                    <TabPanel key={tab} px={0} pb={0}>
                      <HStack mb={2} align="center">
                        <Box
                          w="4px"
                          h="24px"
                          borderRadius="2px"
                          bgGradient="linear(to-r, #121621 1.24%, #536DEC 117.97%)"
                        />
                        <Text fontSize="18px" fontWeight="700" ml={2}>
                          {tab}
                        </Text>
                      </HStack>

                      {description && (
                        <Text
                          fontSize="14px"
                          color="#6B7280"
                          mb={4}
                          whiteSpace="pre-line"
                        >
                          {description}
                        </Text>
                      )}

                      <VStack align="stretch" spacing={4}>
                        {items.length > 0 ? (
                          items.map((item, idx) => {
                            const isDefinition =
                              tab.toLowerCase() === "definition";

                            return isDefinition ? (
                              <Box
                                key={idx}
                                p={3}
                                borderRadius="12px"
                                background="#EDF3FF"
                                border="1px solid #97B1FF"
                              >
                                <Text
                                  fontSize="15px"
                                  color="#1A202C"
                                  whiteSpace="pre-line"
                                >
                                  {item.desc}
                                </Text>
                              </Box>
                            ) : (
                              <HStack
                                key={idx}
                                p={3}
                                borderRadius="12px"
                                border="1px solid #0000001A"
                                spacing={3}
                                align="start"
                              >
                                <VStack
                                  w="40px"
                                  h="40px"
                                  bgGradient="linear(to-r, #121621 1.24%, #536DEC 117.97%)"
                                  borderRadius="14px"
                                  justify="center"
                                >
                                  <Text
                                    fontSize="20px"
                                    fontWeight="600"
                                    color="white"
                                  >
                                    {idx + 1}
                                  </Text>
                                </VStack>

                                <VStack align="stretch" spacing={1} flex="1">
                                  <Text fontWeight="600" color="#1A1A1A">
                                    {item.title || "-"}
                                  </Text>
                                  <Text
                                    fontSize="14px"
                                    color="#6B7280"
                                    mb={2}
                                    whiteSpace="pre-line"
                                  >
                                    {item.desc || "-"}
                                  </Text>

                                  {item.url && drillDownEnabled && (
                                    <HStack align="start">
                                      {" "}
                                      <Button
                                        h="32px"
                                        w="auto"
                                        bg="#536DEC"
                                        color="white"
                                        rightIcon={<ArrowRight size={16} />}
                                        onClick={() => navigate(item.url)}
                                        _hover={
                                          { bg: "#455FDE" }
                                        }
                                        _active={
                                          { bg: "#455FDE" }
                                        }
                                      >
                                        {item.buttonText}
                                      </Button>
                                    </HStack>
                                  )}
                                </VStack>
                              </HStack>
                            );
                          })
                        ) : (
                          <Text color="#6B7280">No items available.</Text>
                        )}
                      </VStack>
                    </TabPanel>
                  );
                })
              ) : (
                <TabPanel>
                  <Text color="#6B7280">No insights available.</Text>
                </TabPanel>
              )}
            </TabPanels>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
